import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { i18n } from '@international/mastodon-i18n';

import { Report } from 'types/report';
import { canEnableCandidateStories } from 'lib/report/candidateStories';
import UpdatePersonalInfoWorkflowCard from './PersonalInfo/UpdatePersonalInfoWorkflowCard';
import { useCandidateStorySubmission } from '../../../api/candidateStories/useCandidateStories';

import ReportAnErrorWorkflowCard from './Disputes/ReportAnErrorWorkflowCard';
import CandidateStoryWorkflowCard from './CandidateStories/WorkflowCard/CandidateStoryWorkflowCard';
import { isReportEligibleForUpdateDL } from './lib/mvr';

export interface CandidateActionWorkflowCardsProps {
  candidateStorySubmitted: boolean;
  disputeSubmitted: boolean;
  report: Report;
  stories?: any;
  submitError?: any;
}

interface State {
  disputeModalOpen: boolean;
  newDisputeModalOpen: boolean;
  candidateStoryOpen: boolean;
  updateDLModalOpen: boolean;
  isSuccessDisputeOpenedOnce: boolean;
  confirmPreviouslyDisputedReportModalOpen: boolean;
}

class CandidateActionWorkflowCards extends Component<
  CandidateActionWorkflowCardsProps,
  State
> {
  constructor(props: CandidateActionWorkflowCardsProps) {
    super(props);

    this.state = {
      disputeModalOpen: false,
      newDisputeModalOpen: false,
      candidateStoryOpen: false,
      updateDLModalOpen: false,
      isSuccessDisputeOpenedOnce: false,
      confirmPreviouslyDisputedReportModalOpen: false,
    };

    this.toggleDisputeModal = this.toggleDisputeModal.bind(this);
    this.toggleNewDisputeModal = this.toggleNewDisputeModal.bind(this);
    this.toggleCandidateStoryModal = this.toggleCandidateStoryModal.bind(this);
    this.toggleUpdateDLModal = this.toggleUpdateDLModal.bind(this);
    this.openSuccessDisputeModal = this.openSuccessDisputeModal.bind(this);
    this.toggleConfirmPreviouslyDisputedReportModal =
      this.toggleConfirmPreviouslyDisputedReportModal.bind(this);
  }

  toggleDisputeModal() {
    // tag this as a dispute flow
    (window as any).hj &&
      (window as any).hj('tagRecording', ['clicked_dispute_button']);
    this.setState(prevState => ({
      disputeModalOpen: !prevState.disputeModalOpen,
    }));
  }

  toggleNewDisputeModal() {
    this.setState(prevState => ({
      newDisputeModalOpen: !prevState.newDisputeModalOpen,
    }));
  }

  toggleCandidateStoryModal() {
    this.setState(prevState => ({
      candidateStoryOpen: !prevState.candidateStoryOpen,
    }));
  }

  toggleUpdateDLModal() {
    this.setState(prevState => ({
      updateDLModalOpen: !prevState.updateDLModalOpen,
    }));
  }

  openSuccessDisputeModal() {
    this.setState(prevState => ({
      isSuccessDisputeOpenedOnce: !prevState.isSuccessDisputeOpenedOnce,
      disputeModalOpen: !prevState.disputeModalOpen,
    }));
  }

  toggleConfirmPreviouslyDisputedReportModal() {
    this.setState(prevState => ({
      confirmPreviouslyDisputedReportModalOpen:
        !prevState.confirmPreviouslyDisputedReportModalOpen,
    }));
  }

  render() {
    const {
      report,
      disputeSubmitted,
      candidateStorySubmitted,
      submitError,
      stories,
    } = this.props;
    // Whether the candidate can share a story follows from the report.
    const candidateStoriesEnabled = !!canEnableCandidateStories(report);

    const {
      disputeModalOpen,
      candidateStoryOpen,
      newDisputeModalOpen,
      updateDLModalOpen,
      isSuccessDisputeOpenedOnce,
      confirmPreviouslyDisputedReportModalOpen,
    } = this.state;
    const RenderCandidateStoryWorkflowCard = (
      <>
        {candidateStoriesEnabled && (
          <CandidateStoryWorkflowCard
            toggleDisputeModal={this.toggleDisputeModal}
            stories={stories}
            report={report}
            candidateStorySubmitted={candidateStorySubmitted}
            // Nothing has ever turned this on (it was always set to false), so
            // the story form always asks for the full set of information.
            candidateStoriesGeneralInfoOnly={false}
            toggleCandidateStoryModal={this.toggleCandidateStoryModal}
            isOpen={candidateStoryOpen}
            submitError={submitError}
          />
        )}
      </>
    );

    const RenderReportAnErrorWorkflowCard = (
      <ReportAnErrorWorkflowCard
        candidateStoriesEnabled={candidateStoriesEnabled}
        disputeSubmitted={disputeSubmitted}
        isOpen={disputeModalOpen}
        isNewOpen={newDisputeModalOpen}
        isCPDOpen={confirmPreviouslyDisputedReportModalOpen}
        updateDLModalOpen={updateDLModalOpen}
        isSuccessDisputeOpenedOnce={isSuccessDisputeOpenedOnce}
        report={report}
        toggleCandidateStoryModal={this.toggleCandidateStoryModal}
        toggleDisputeModal={this.toggleDisputeModal}
        toggleNewDisputeModal={this.toggleNewDisputeModal}
        toggleUpdateDLModal={this.toggleUpdateDLModal}
        openSuccessDisputeModal={this.openSuccessDisputeModal}
        toggleConfirmPreviouslyDisputedReportModal={
          this.toggleConfirmPreviouslyDisputedReportModal
        }
      />
    );

    return (
      <div
        role='list'
        aria-label={i18n.getStr('component.Report.CandidateActions.title')}
      >
        {isReportEligibleForUpdateDL(report) && (
          <UpdatePersonalInfoWorkflowCard
            report={report}
            isDialogOpen={updateDLModalOpen}
            toggleShowModal={this.toggleUpdateDLModal}
          />
        )}

        {RenderCandidateStoryWorkflowCard}
        {RenderReportAnErrorWorkflowCard}
      </div>
    );
  }

  static propTypes = {
    candidateStorySubmitted: PropTypes.bool.isRequired,
    disputeSubmitted: PropTypes.bool.isRequired,
    report: PropTypes.object.isRequired,
    submitError: PropTypes.bool.isRequired,
    stories: PropTypes.array.isRequired,
  };
}

// The candidate stories state is the report's, in the query cache; the
// dispute one is still Redux's.
const CandidateActionWorkflowCardsContainer = ({
  report,
}: {
  report: Report;
}) => {
  const { candidateStorySubmitted, stories, submitError } =
    useCandidateStorySubmission(report);
  const disputeSubmitted = useSelector(
    (state: any) => !!state.portal.disputeSubmitted,
  );

  return (
    <CandidateActionWorkflowCards
      report={report}
      candidateStorySubmitted={candidateStorySubmitted}
      disputeSubmitted={disputeSubmitted}
      stories={stories}
      submitError={submitError}
    />
  );
};

export default CandidateActionWorkflowCardsContainer;
